/**
 * Every write the app makes. Each goes to Dexie AND the outbox in one
 * transaction, then nudges sync — the screen updates instantly, the server
 * hears about it when there's a network.
 */

import { TABLES, db, type CustomFoodRow, type Entity, type FoodRow, type MealItem, type ProfileRow,
  type RecipeRow, type SavedMealRow, type TargetRow, type WeighinRow, type CheckinRow } from '.'
import { newId, stableId } from '../lib/ids'
import { clean, macroColumns, scale, type Nutrients } from '../lib/nutrients'
import { recipeTotals } from '../lib/recipe'
import { syncSoon } from '../sync/engine'

const now = () => new Date().toISOString()

async function stage<T extends { id: string }>(entity: Entity, row: T, op: 'upsert' | 'delete' = 'upsert') {
  const table = TABLES[entity]
  const stamped = { ...row, updated_at: now(), ...(op === 'delete' ? { deleted_at: now() } : {}) }
  await db.transaction('rw', table, db.outbox, async () => {
    await table.put(stamped)
    await db.outbox.add({
      op_id: newId(), entity, op, row_id: row.id, payload: stamped as Record<string, unknown>,
      created_at: Date.now(), attempts: 0,
    })
  })
  syncSoon()
  return stamped
}

// --- food log ---------------------------------------------------------------

export type NewFood = {
  name: string
  brand?: string | null
  source: FoodRow['source']
  source_ref?: string | null
  grams?: number | null
  serving_label?: string | null
  serving_qty?: number | null
  per100?: Nutrients | null
  /** Totals, used when there's no per100 (quick add, AI item without grams). */
  nutrients?: Nutrients | null
  ai_confidence?: FoodRow['ai_confidence']
  notes?: string | null
}

export function totalsFor(f: { per100?: Nutrients | null; grams?: number | null; nutrients?: Nutrients | null }): Nutrients {
  if (f.per100 && f.grams !== null && f.grams !== undefined) return scale(clean(f.per100), f.grams)
  return clean(f.nutrients ?? {}, false)
}

function foodRow(f: NewFood, day: string, meal: number, order: number,
  group?: { id: string; label: string } | null): FoodRow {
  const totals = totalsFor(f)
  return {
    id: newId(), updated_at: now(), day, meal_slot: meal, order_index: order,
    logged_at: now(), source: f.source, source_ref: f.source_ref ?? null,
    name: f.name.slice(0, 120) || 'Food', brand: f.brand ?? null,
    grams: f.grams ?? null, serving_label: f.serving_label ?? null, serving_qty: f.serving_qty ?? null,
    per100: f.per100 ? clean(f.per100) : null, nutrients: totals, ...macroColumns(totals),
    group_id: group?.id ?? null, group_label: group?.label ?? null,
    ai_confidence: f.ai_confidence ?? null, notes: f.notes ?? null,
  }
}

async function nextOrder(day: string, meal: number): Promise<number> {
  const rows = await db.foods.where('[day+meal_slot]').equals([day, meal]).toArray()
  return rows.reduce((m, r) => Math.max(m, r.order_index + 1), 0)
}

export async function logFoods(items: NewFood[], day: string, meal: number, groupLabel?: string | null) {
  let order = await nextOrder(day, meal)
  const group = groupLabel && items.length > 1 ? { id: newId(), label: groupLabel } : null
  for (const f of items) await stage('food', foodRow(f, day, meal, order++, group))
}

export async function updateFood(row: FoodRow, patch: Partial<FoodRow>) {
  const next = { ...row, ...patch }
  const totals = totalsFor(next)
  await stage('food', { ...next, nutrients: totals, ...macroColumns(totals) })
}

export async function deleteFood(row: FoodRow) {
  await stage('food', row, 'delete')
}

export async function copyFoods(rows: FoodRow[], day: string, meal: number) {
  await logFoods(rows.map((r) => ({
    name: r.name, brand: r.brand, source: r.source, source_ref: r.source_ref, grams: r.grams,
    serving_label: r.serving_label, serving_qty: r.serving_qty, per100: r.per100, nutrients: r.nutrients,
  })), day, meal)
}

export async function setDayStatus(day: string, status: 'auto' | 'complete' | 'incomplete', userId: number) {
  await stage('day_status', { id: await stableId(`${userId}:day:${day}`), day, status, updated_at: now() })
}

// --- foods, recipes, meals ----------------------------------------------------

export async function saveCustomFood(row: Omit<CustomFoodRow, 'id' | 'updated_at'> & { id?: string }) {
  return stage('custom_food', { ...row, id: row.id ?? newId(), updated_at: now(), per100: clean(row.per100 ?? {}) })
}

export async function deleteCustomFood(row: CustomFoodRow) { await stage('custom_food', row, 'delete') }

export async function touchCustomFood(id: string) {
  const row = await db.custom_foods.get(id)
  if (row) await stage('custom_food', { ...row, last_used_at: now() })
}

export async function saveRecipe(row: Omit<RecipeRow, 'id' | 'updated_at'> & { id?: string }) {
  const t = recipeTotals(row.ingredients ?? [], row.servings, row.cooked_weight_g)
  return stage('recipe', { ...row, id: row.id ?? newId(), updated_at: now(),
    per100: t.per100, per_serving: t.perServing, total_grams: t.totalGrams })
}

export async function deleteRecipe(row: RecipeRow) { await stage('recipe', row, 'delete') }

export async function saveMeal(name: string, items: MealItem[], id?: string) {
  return stage('saved_meal', { id: id ?? newId(), name, items, updated_at: now() } as SavedMealRow)
}

export async function deleteMeal(row: SavedMealRow) { await stage('saved_meal', row, 'delete') }

export async function touch(entity: 'recipe' | 'saved_meal', id: string) {
  const table = TABLES[entity]
  const row = await table.get(id)
  if (row) await stage(entity, { ...row, last_used_at: now() })
}

// --- body, plan -----------------------------------------------------------------

export async function addWeighin(kg: number, measuredAt: string, notes?: string | null) {
  await stage('weighin', { id: newId(), measured_at: measuredAt, weight_kg: kg, notes: notes ?? null,
    relay_state: 'pending', updated_at: now() } as WeighinRow)
}

export async function deleteWeighin(row: WeighinRow) {
  await stage('weighin', { ...row, relay_state: 'pending' }, 'delete')
}

export async function saveProfile(userId: number, patch: Partial<ProfileRow>) {
  const id = await stableId(`${userId}:profile`)
  const existing = (await db.profiles.get(id)) ?? { id, updated_at: now() }
  return stage('profile', { ...existing, ...patch, id })
}

export async function addTarget(t: Omit<TargetRow, 'id' | 'updated_at'>) {
  return stage('target', { ...t, id: newId(), updated_at: now() })
}

export async function addCheckin(c: Omit<CheckinRow, 'id' | 'updated_at'>) {
  return stage('checkin', { ...c, id: newId(), updated_at: now() })
}
